import React from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-hot-toast'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Text,
  makeStyles,
  Checkbox,
  Tooltip,
  Spinner
} from '@fluentui/react-components'
import {
  DocumentFolder24Regular,
  Delete24Regular
} from '@fluentui/react-icons'

/**
 * Defines the Fluent UI styles for the DocLibFilter component.
 * Utilizes CSS-in-JS to create scoped class names for layout, dropdowns,
 * tooltips, and responsive wrapping.
 */
const useStyles = makeStyles({
  dialogSurface: {
    overflow: 'scroll',
    overflowX: 'hidden',
    padding: '10px',
    width: '90vw',
    maxWidth: '500px'
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  listContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px'
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '2px 2px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f9f9f9'
  },
  headerText: {
    fontWeight: '600',
    flexGrow: 1
  },
  headerTokens: {
    fontWeight: '600',
    minWidth: '80px',
    textAlign: 'right'
  },
  fileList: {
    listStyleType: 'none',
    paddingLeft: 0,
    margin: 0,
    maxHeight: '149px',
    overflowY: 'auto'
  },
  fileListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '2px 2px',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  fileInfo: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    overflow: 'hidden'
  },
  fileIcon: {
    flexShrink: 0,
    color: '#605e5c'
  },
  fileNameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    fontWeight: '400',
    display: 'block'
  },
  tokenText: {
    minWidth: '80px',
    textAlign: 'right',
    color: '#605e5c'
  }
})

/**
 * ExistingFilesModal Component
 *
 * A dialog component that displays a list of files previously uploaded by the user.
 * It allows the user to manage their storage by deleting files, checking token usage,
 * or proceeding to upload new files.
 *
 * @param {Object} props - The component properties.
 * @param {boolean} props.isOpen - Controls whether the modal is currently visible.
 * @param {Function} props.onClose - Callback function triggered to close the modal.
 * @param {Array} props.files - Array of file objects currently stored on the server.
 * @param {Function} props.onUploadNew - Callback triggered when the "Upload New File(s)" button is clicked.
 * @param {Function} props.onToggleFileSelection - Callback to toggle the selection state of an individual file.
 * @param {Function} props.onSelectAll - Callback to select or deselect all files in the list.
 * @param {Function} props.onDelete - Callback triggered to delete the currently selected files.
 * @param {boolean} props.isDeleting - Indicates if a deletion operation is currently in progress.
 * @param {number} props.totalToken - The total number of tokens consumed by the currently uploaded files.
 * @param {number} props.maxTokenLimit - The maximum allowed token capacity for the user.
 */
const ExistingFilesModal = ({
  isOpen,
  onClose,
  files,
  onUploadNew,
  onToggleFileSelection,
  onSelectAll,
  onDelete,
  isDeleting,
  totalToken,
  maxTokenLimit
}) => {
  const styles = useStyles()

  const selectedFilesCount = files.filter(f => f.isSelected).length
  const areAllFilesSelected =
    files.length > 0 && selectedFilesCount === files.length
  // Calculate if currently exceeding limits
  const isTokenLimitExceeded =
    maxTokenLimit != null && totalToken > maxTokenLimit

  /**
  * Wrapper for the onClose callback.
  * Intercepts closing attempts to ensure the user cannot dismiss the modal
  * if they are currently exceeding their token quota, forcing them to delete files first.
  */
  const handleClose = () => {
    if (isTokenLimitExceeded) {
      toast.error(
        'You are over your token quota! Please delete files to get under the limit.',
        { id: 'existing-modal-lock' }
      )
      return
    }
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(e, data) => !data.open && handleClose()}
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle>Previously Uploaded Files</DialogTitle>
          <Text>
            Your documents are ready. Ask a question or upload new files.
          </Text>

          {/* The entire list structure is improved */}
          <div className={styles.listContainer}>
            {/* HEADER ROW */}
            <div className={styles.listHeader}>
              <Checkbox
                checked={areAllFilesSelected}
                onChange={() => onSelectAll(!areAllFilesSelected)}
                disabled={files.length === 0 || isDeleting}
                aria-label='Select all files'
              />
              <Text className={styles.headerText}>File Name</Text>
              <Text className={styles.headerTokens}>
                Total Tokens ({totalToken != null
                  ? totalToken.toLocaleString()
                  : 'N/A'})
              </Text>
            </div>

            {/* THE FILE LIST */}
            <ul className={styles.fileList}>
              {files.map(file =>
                <li key={file.name} className={styles.fileListItem}>
                  <Checkbox
                    checked={file.isSelected}
                    onChange={() => onToggleFileSelection(file.name)}
                    disabled={isDeleting}
                  />
                  <div className={styles.fileInfo}>
                    <DocumentFolder24Regular className={styles.fileIcon} />
                    {/* <Text>{file.name}</Text> */}
                    <Tooltip content={file.name} relationship='label'>
                      <Text className={styles.fileNameText}>
                        {file.name}
                      </Text>
                    </Tooltip>
                  </div>
                  <Text className={styles.tokenText}>
                    {file.tokens != null ? file.tokens.toLocaleString() : 'N/A'}
                  </Text>
                </li>
              )}
              {files.length === 0 &&
                <Text
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    display: 'block'
                  }}
                >
                  No files remaining. You can close this or upload a new set.
                </Text>}
            </ul>
          </div>

          {/* Red warning box if limit is exceeded */}
          {isTokenLimitExceeded &&
            <div
              style={{
                backgroundColor: '#fde7e9',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #d13438',
                marginBottom: '10px',
                marginTop: '10px'
              }}
            >
              <Text weight='semibold' style={{ color: '#d13438' }}>
                Token Quota Exceeded: {totalToken.toLocaleString()} /{' '}
                {maxTokenLimit.toLocaleString()}
              </Text>
              <Text
                block
                style={{ color: '#d13438', fontSize: '12px', marginTop: '4px' }}
              >
                You must delete files to free up space before you can close this
                window or upload new files.
              </Text>
            </div>}

          <DialogActions>
            <Button
              appearance='secondary'
              onClick={onDelete}
              disabled={selectedFilesCount === 0 || isDeleting}
              icon={isDeleting ? <Spinner size='tiny' /> : <Delete24Regular />}
            >
              {isDeleting ? 'Deleting...' : `Delete (${selectedFilesCount})`}
            </Button>
            <div style={{ flexGrow: 1 }} />
            {/* Disable the close button if over limit */}
            <Button
              appearance='secondary'
              onClick={handleClose}
              disabled={isDeleting || isTokenLimitExceeded}
            >
              Close
            </Button>

            <Tooltip
              content={
                isTokenLimitExceeded
                  ? 'Delete files to enable upload'
                  : 'Clear selection to upload new files'
              }
              relationship='label'
              withArrow
              disabled={selectedFilesCount === 0 && !isTokenLimitExceeded}
            >
              {/*
                This span is a common and reliable pattern. A disabled button
                doesn't fire mouse events, so the Tooltip can't detect a hover.
                By wrapping it in a span, the Tooltip attaches to the span,
                which is NOT disabled, and everything works correctly.
              */}
              <span>
                <Button
                  style={{ width: '100%' }}
                  appearance='primary'
                  onClick={onUploadNew}
                  disabled={isDeleting || isTokenLimitExceeded}
                >
                  Upload New File(s)
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

ExistingFilesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      isSelected: PropTypes.bool.isRequired,
      tokens: PropTypes.number
    })
  ).isRequired,
  onUploadNew: PropTypes.func.isRequired,
  onToggleFileSelection: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  totalToken: PropTypes.number,
  maxTokenLimit: PropTypes.number
}

export default ExistingFilesModal
